import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { mockData } from '../mock';
import { Music, Mail, MapPin, Clock, Award, Users, Target, Check, ExternalLink, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Home = () => {
  const contactRecaptchaRef = useRef();
  const bookingRecaptchaRef = useRef();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instrument: 'Guitar',
    message: '',
    website: '' // Honeypot field
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    instrument: 'Guitar',
    preferredDay: '',
    preferredTime: '',
    website: '' // Honeypot field
  });
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);

  // Fetch available time slots on component mount
  React.useEffect(() => {
    const fetchAvailableSlots = async () => {
      try {
        const response = await axios.get(`${API}/timeslots`);
        setAvailableSlots(response.data.filter(slot => slot.isAvailable));
      } catch (error) {
        console.error('Error fetching available slots:', error);
      }
    };
    fetchAvailableSlots();
  }, []);

  // Get time slots based on day from available slots
  const getTimeSlots = (day) => {
    return availableSlots
      .filter(slot => slot.day === day)
      .map(slot => slot.time);
  };

  const handleDayChange = (day) => {
    setBookingData({
      ...bookingData,
      preferredDay: day,
      preferredTime: '' // Reset time when day changes
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot field - if filled, it's a bot
    if (formData.website) {
      console.log('Bot detected - honeypot filled');
      return;
    }
    
    setIsSubmittingContact(true);
    
    try {
      // Get reCAPTCHA token from the ref value
      const recaptchaToken = contactRecaptchaRef.current?.getValue();
      
      if (!recaptchaToken) {
        toast.error('Please complete the reCAPTCHA verification');
        setIsSubmittingContact(false);
        return;
      }
      
      // Remove honeypot field and add recaptchaToken before sending
      const { website, ...submitData } = formData;
      await axios.post(`${API}/contact`, { ...submitData, recaptchaToken });
      
      toast.success('Message Sent!', {
        description: "We'll get back to you within 24 hours.",
      });
      
      setFormData({ name: '', email: '', instrument: 'Guitar', message: '', website: '' });
      contactRecaptchaRef.current?.reset();
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error.response?.data?.detail || 'Please try again or contact us directly.';
      toast.error('Failed to send message', {
        description: errorMessage,
      });
      contactRecaptchaRef.current?.reset();
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot field - if filled, it's a bot
    if (bookingData.website) {
      console.log('Bot detected - honeypot filled');
      return;
    }
    
    setIsSubmittingBooking(true);
    
    try {
      // Get reCAPTCHA token from the ref value
      const recaptchaToken = bookingRecaptchaRef.current?.getValue();
      
      if (!recaptchaToken) {
        toast.error('Please complete the reCAPTCHA verification');
        setIsSubmittingBooking(false);
        return;
      }
      
      // Remove honeypot field and add recaptchaToken before sending
      const { website, ...submitData } = bookingData;
      await axios.post(`${API}/bookings`, { ...submitData, recaptchaToken });
      
      toast.success('Trial Lesson Booked!', {
        description: "We'll confirm your booking shortly via email.",
      });
      
      setBookingData({ name: '', email: '', instrument: 'Guitar', preferredDay: '', preferredTime: '', website: '' });
      bookingRecaptchaRef.current?.reset();
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      const errorMessage = error.response?.data?.detail || 'Please try again or contact us directly.';
      toast.error('Failed to book trial lesson', {
        description: errorMessage,
      });
      bookingRecaptchaRef.current?.reset();
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md fixed w-full top-0 z-50 border-b border-amber-100">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Music className="w-8 h-8 text-amber-600" />
            <span className="text-xl md:text-2xl font-bold text-gray-800">Guitar 101 Tuition</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-8">
            <a href="#home" className="text-gray-700 hover:text-amber-600 transition-colors">Home</a>
            <a href="#pricing" className="text-gray-700 hover:text-amber-600 transition-colors">Pricing</a>
            <a href="#instructor" className="text-gray-700 hover:text-amber-600 transition-colors">Instructor</a>
            <a href="/testimonials" className="text-gray-700 hover:text-amber-600 transition-colors">Testimonials</a>
            <a href="#faq" className="text-gray-700 hover:text-amber-600 transition-colors">FAQ</a>
            <a href="#contact" className="text-gray-700 hover:text-amber-600 transition-colors">Contact</a>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-gray-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <Button className="bg-amber-600 hover:bg-amber-700 text-white transition-all hover:scale-105 text-sm md:text-base">
              <a href="#book-trial">Book Free Trial</a>
            </Button>
          </div>
        </nav>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-amber-100 py-4 px-4 space-y-1 shadow-lg">
            <a href="#home" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 text-gray-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">Home</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 text-gray-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">Pricing</a>
            <a href="#instructor" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 text-gray-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">Instructor</a>
            <a href="/testimonials" className="block py-3 px-4 text-amber-600 font-medium hover:bg-amber-50 rounded-lg transition-colors">Testimonials</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 text-gray-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">FAQ</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 text-gray-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">Contact</a>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Master Your
                <span className="text-amber-600"> Musical Journey</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Professional guitar, bass, and ukulele tuition for all skill levels. 
                Learn from an experienced instructor and unlock your musical potential.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white transition-all hover:scale-105 hover:shadow-lg">
                  <a href="#book-trial">Start Your Free Trial</a>
                </Button>
                <Button size="lg" variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50 transition-all">
                  <a href="#pricing">View Pricing</a>
                </Button>
              </div>
              <div className="flex gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  <span className="text-gray-700 font-medium">{mockData.stats.students} Students</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  <span className="text-gray-700 font-medium">{mockData.stats.experience}+ Years Experience</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_1b3b5244-ed62-4f83-939c-0af5b3a8c34d/artifacts/43l0jhaq_image.png" 
                alt="Guitar 101 Tuition" 
                className="w-full max-w-md rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Teaching Philosophy Section - Central Focus */}
      <section className="py-20 px-4 bg-gradient-to-r from-amber-600 to-amber-700">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-8">
              <Music className="w-5 h-5 text-white" />
              <span className="text-white font-medium">My Teaching Philosophy</span>
            </div>
            <blockquote className="text-2xl md:text-3xl lg:text-4xl text-white font-medium leading-relaxed mb-8">
              At Guitar 101 Tuition, learning an instrument is about more than memorising chords and practising exercises.
            </blockquote>
            <p className="text-lg md:text-xl text-amber-100 leading-relaxed mb-8">
              My goal is to help students develop the skills, confidence and musical understanding to play complete songs, enjoy music independently and, if they choose, play alongside other musicians.
            </p>
            <p className="text-lg md:text-xl text-amber-100 leading-relaxed mb-10">
              Through personalised lessons, structured progression and practical playing experience, students receive the guidance they need to keep developing.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-3 bg-white/10 rounded-full px-6 py-3">
                <Target className="w-6 h-6 text-white" />
                <span className="text-white font-medium">Personalised Lessons</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-full px-6 py-3">
                <Award className="w-6 h-6 text-white" />
                <span className="text-white font-medium">Structured Progression</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-full px-6 py-3">
                <Users className="w-6 h-6 text-white" />
                <span className="text-white font-medium">Practical Experience</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Guitar 101 Tuition?</h2>
            <p className="text-gray-600 text-lg">We provide comprehensive music education tailored to your needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.features.map((feature, index) => (
              <Card key={index} className="border-amber-100 hover:border-amber-300 transition-all hover:shadow-lg group">
                <CardHeader>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-200 transition-colors">
                    <Target className="w-6 h-6 text-amber-600" />
                  </div>
                  <CardTitle className="text-xl text-gray-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Instruments Section */}
      <section id="instruments" className="py-20 px-4 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Instruments We Teach</h2>
            <p className="text-gray-600 text-lg">Find the perfect instrument to begin your musical journey</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Guitar Card */}
            <Card className="border-amber-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="h-56 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1620502976908-c92b3d53ef38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxzdW5idXJzdCUyMHN0cmF0b2Nhc3RlciUyMGJvZHklMjBwaWNrdXBzJTIwY2xvc2V8ZW58MHx8fHwxNzcxMzM3MTE0fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Electric Stratocaster Guitar"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900 flex items-center gap-2">
                  <Music className="w-6 h-6 text-amber-600" />
                  Guitar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-600">
                  Whether you dream of strumming acoustic ballads or shredding electric solos, guitar is the perfect starting point. Learn chords, fingerpicking, lead techniques, and music theory in a fun, supportive environment.
                </p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Acoustic & Electric styles
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Beginner to advanced levels
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    All genres covered
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Bass Card */}
            <Card className="border-amber-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="h-56 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1642523937194-63194c4748e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxiYXNzJTIwZ3VpdGFyJTIwaW5zdHJ1bWVudCUyMG11c2ljfGVufDB8fHx8MTc3MTMzNjQ5N3ww&ixlib=rb-4.1.0&q=85" 
                  alt="Bass Guitar"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900 flex items-center gap-2">
                  <Music className="w-6 h-6 text-amber-600" />
                  Bass
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-600">
                  The backbone of every great band! Learn to lay down grooves that make people move. Discover how the bass brings music to life.
                </p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Fingerstyle and Picking Techniques
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Rhythm & groove mastery
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Band-ready skills
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Ukulele Card */}
            <Card className="border-amber-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="h-56 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1587337657879-28f085cda1e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwyfHx1a3VsZWxlJTIwaW5zdHJ1bWVudCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcxMzM2NTExfDA&ixlib=rb-4.1.0&q=85" 
                  alt="Ukulele"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900 flex items-center gap-2">
                  <Music className="w-6 h-6 text-amber-600" />
                  Ukulele
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-600">
                  The friendliest instrument to learn! Perfect for beginners and those who want quick wins. With just four strings, you'll be playing your first song in your very first lesson.
                </p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Easy to learn & portable
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Great for all ages
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-600" />
                    Play a song from day one
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Lesson Pricing</h2>
            <p className="text-gray-600 text-lg">Professional one-on-one instruction with comprehensive learning materials</p>
          </div>
          <div className="flex justify-center">
            <div className="max-w-md w-full">
              {mockData.pricing.map((plan, index) => (
                <Card 
                  key={index} 
                  className="relative border-2 border-amber-500 shadow-xl"
                >
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-amber-600 text-white px-4 py-1 rounded-full text-sm font-semibold">Professional Tuition</span>
                  </div>
                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl text-gray-900 mb-2">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-600">/{plan.period}</span>
                    </div>
                    <CardDescription className="mt-2">{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white transition-all hover:scale-105">
                      <a href="#book-trial">Book Free Trial</a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Bio Section */}
      <section id="instructor" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Meet Your Instructor</h2>
              <p className="text-gray-600 text-lg">Learn from a passionate and experienced music educator</p>
            </div>
            <Card className="border-amber-100">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="flex flex-col justify-start items-center">
                    <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-amber-200 shadow-lg">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_guitar101-tuition/artifacts/e2dl4ok0_Adrian%20with%20Fender.jpg" 
                        alt="Adrian Gravelle - Instructor" 
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <a 
                      href="https://musicteacher.com/profile/adrian1" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-20 inline-block hover:opacity-80 transition-opacity"
                      data-testid="musicteacher-profile-link"
                    >
                      <img 
                        src="https://customer-assets.emergentagent.com/job_guitar101-tuition/artifacts/ou6aaov3_music-teacher-verified-teacher-badge.png" 
                        alt="Official MusicTeacher.com Member - View Profile"
                        className="h-20 w-auto"
                      />
                    </a>
                  </div>
                  <div className="md:col-span-2 space-y-4">
                    <h3 className="text-2xl font-bold text-gray-900">{mockData.instructor.name}</h3>
                    <p className="text-amber-600 font-semibold">{mockData.instructor.title}</p>
                    <p className="text-gray-700 leading-relaxed">{mockData.instructor.bio}</p>
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-900">What I Bring:</h4>
                        <ul className="space-y-1">
                          {mockData.instructor.qualifications.map((qual, idx) => (
                            <li key={idx} className="text-gray-600 text-sm flex items-start gap-2">
                              <Check className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              {qual}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-900">Specialties:</h4>
                        <ul className="space-y-1">
                          {mockData.instructor.specialties.map((spec, idx) => (
                            <li key={idx} className="text-gray-600 text-sm flex items-start gap-2">
                              <Check className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              {spec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Book Free Trial Section */}
      <section id="book-trial" className="py-20 px-4 bg-gradient-to-br from-amber-100 via-amber-50 to-white">
        <div className="container mx-auto max-w-2xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Book Your Free Trial Lesson</h2>
            <p className="text-gray-600 text-lg">Experience our teaching style with a complimentary session</p>
          </div>
          <Card className="border-amber-200 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleBookingSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="booking-name">Full Name *</Label>
                    <Input 
                      id="booking-name"
                      placeholder="John Doe" 
                      value={bookingData.name}
                      onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                      required
                      className="border-amber-200 focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-email">Email *</Label>
                    <Input 
                      id="booking-email"
                      type="email"
                      placeholder="john@example.com" 
                      value={bookingData.email}
                      onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                      required
                      className="border-amber-200 focus:border-amber-500"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="booking-instrument">Instrument *</Label>
                    <select 
                      id="booking-instrument"
                      value={bookingData.instrument}
                      onChange={(e) => setBookingData({...bookingData, instrument: e.target.value})}
                      required
                      className="w-full h-10 px-3 rounded-md border border-amber-200 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                    >
                      <option value="Guitar">Guitar</option>
                      <option value="Bass">Bass</option>
                      <option value="Ukulele">Ukulele</option>
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="booking-day">Preferred Day *</Label>
                    <select 
                      id="booking-day"
                      value={bookingData.preferredDay}
                      onChange={(e) => handleDayChange(e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-md border border-amber-200 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                    >
                      <option value="">Select a day</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Saturday">Saturday</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking-time">Preferred Time *</Label>
                    <select 
                      id="booking-time"
                      value={bookingData.preferredTime}
                      onChange={(e) => setBookingData({...bookingData, preferredTime: e.target.value})}
                      required
                      disabled={!bookingData.preferredDay}
                      className="w-full h-10 px-3 rounded-md border border-amber-200 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select a time</option>
                      {getTimeSlots(bookingData.preferredDay).map((slot) => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Honeypot field - hidden from users, but bots will fill it */}
                <input
                  type="text"
                  name="website"
                  value={bookingData.website}
                  onChange={(e) => setBookingData({...bookingData, website: e.target.value})}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                {/* reCAPTCHA - visible checkbox */}
                <ReCAPTCHA
                  ref={bookingRecaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                />
                <Button 
                  type="submit" 
                  disabled={isSubmittingBooking}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6 transition-all hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmittingBooking ? 'Booking...' : 'Book My Free Trial Lesson'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 text-lg">Got questions? We've got answers!</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {mockData.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-amber-100 rounded-lg px-6 hover:border-amber-300 transition-colors">
                <AccordionTrigger className="text-left text-gray-900 hover:text-amber-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-br from-white via-amber-50 to-amber-100">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-gray-600 text-lg">Have questions? We'd love to hear from you!</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <p className="text-gray-600">{mockData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Location</p>
                      <p className="text-gray-600">{mockData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Hours</p>
                      <p className="text-gray-600">{mockData.contact.hours}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Card className="border-amber-200 shadow-xl">
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input 
                      id="name"
                      placeholder="Your name" 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="border-amber-200 focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email"
                      type="email"
                      placeholder="your@email.com" 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="border-amber-200 focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="instrument">Instrument of Interest *</Label>
                    <select 
                      id="instrument"
                      value={formData.instrument}
                      onChange={(e) => setFormData({...formData, instrument: e.target.value})}
                      required
                      className="w-full h-10 px-3 rounded-md border border-amber-200 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                    >
                      <option value="Guitar">Guitar</option>
                      <option value="Bass">Bass</option>
                      <option value="Ukulele">Ukulele</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message"
                      placeholder="Tell us about your musical goals..." 
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      rows={4}
                      className="border-amber-200 focus:border-amber-500"
                    />
                  </div>
                  {/* Honeypot field - hidden from users, but bots will fill it */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                  {/* reCAPTCHA - visible checkbox */}
                  <ReCAPTCHA
                    ref={contactRecaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                  />
                  <Button 
                    type="submit" 
                    disabled={isSubmittingContact}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingContact ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Music className="w-6 h-6 text-amber-500" />
                <span className="text-xl font-bold">Guitar 101 Tuition</span>
              </div>
              <p className="text-gray-400">Empowering musicians of all levels to achieve their musical dreams through expert instruction.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-amber-500 transition-colors">Home</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-amber-500 transition-colors">Pricing</a></li>
                <li><a href="#instructor" className="text-gray-400 hover:text-amber-500 transition-colors">Instructor</a></li>
                <li><a href="#faq" className="text-gray-400 hover:text-amber-500 transition-colors">FAQ</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-amber-500 transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Instruments We Teach</h4>
              <ul className="space-y-2 text-gray-400">
                <li>• Guitar (Acoustic & Electric)</li>
                <li>• Bass Guitar</li>
                <li>• Ukulele</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Guitar 101 Tuition. All rights reserved.</p>
            <a 
              href="/admin" 
              className="mt-4 md:mt-0 text-gray-500 hover:text-amber-500 transition-colors text-sm"
              data-testid="footer-admin-link"
            >
              Admin Login
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;